import React from "react";
import {
  Box,
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

export default function PasoDatos({ data, update, back, next }) {

  // 🔹 helper único para actualizar estado
  const handleChange = (field, value) => {
    update({ [field]: value });
  };

  const continuar = () => next();

  const tfSx = {
    "& .MuiInputBase-root": { borderRadius: 2, minHeight: 44 },
    minWidth: 200
  };

  const campoPendiente = (valor) =>
    data.datosAutocompletados &&
    (!valor || String(valor).trim() === "");

  const isValidEmail = (email) => {
    if (!email) return true; // vacío no es error aquí
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidCelular = (cel) => {
    if (!cel) return false; // celular es obligatorio
    return /^3\d{9}$/.test(cel);
  };

  const isValidTelefonoFijo = (tel) => {
    if (!tel) return true; // fijo NO obligatorio
    return /^\d{7,10}$/.test(tel);
  };

  const isStepValid = () => {
    // 1. Campos obligatorios completos
    for (const field of requiredFields) {
      if (!data[field] || String(data[field]).trim() === "") {
        return false;
      }
    }

    // 2. Validaciones específicas
    if (!isValidCelular(data.celular)) return false;
    if (data.correoPersonal && !isValidEmail(data.correoPersonal)) return false;

    return true;
  };

  const requiredFields = [
    "nombres",
    "fechaExpedicion",
    "ciudadExpedicion",
    "genero",
    "fechaNacimiento",
    "lugarNacimiento",
    "estadoCivil",
    "departamento",
    "municipio",
    "barrio",
    "direccion",
    "municipio",
    "celular",
    "correoPersonal"
  ];

  const toTitleCase = (text = "") =>
    text
      .toLowerCase()
      .replace(/(^|\s)(\p{L})/gu, (_, space, letter) => space + letter.toUpperCase());


  return (
    <Box sx={{ maxWidth: "min(1200px, 95%)", margin: "0 auto" }}>
      <Typography variant="h6" fontWeight={600} mb={3}>
        Datos Personales
      </Typography>

      <Grid container spacing={2}>

        {/* 1. Nombres */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            required
            label="Nombres y apellidos"
            fullWidth
            sx={tfSx}
            value={toTitleCase(data.nombres ?? "")}
            onChange={(e) => handleChange("nombres", toTitleCase(e.target.value))}
          />
        </Grid>

        {/* 2. Tipo identificación */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            disabled
            label="Tipo de identificación"
            fullWidth
            sx={tfSx}
            value={data.tipoIdentidad ?? ""}
            onChange={(e) => handleChange("tipoIdentidad", e.target.value)}
          >
            <MenuItem value="CC">Cédula de ciudadanía</MenuItem>
            <MenuItem value="CE">Cédula de extranjería</MenuItem>
            <MenuItem value="PA">Pasaporte</MenuItem>
          </TextField>
        </Grid>

        {/* 3. Número identificación (autocompletado) */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            disabled
            label="Número de identificación"
            fullWidth
            sx={tfSx}
            InputProps={{ readOnly: true }}
            value={data.numeroDocumento ?? ""}
          />
        </Grid>

        {/* 4. Fecha expedición */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            required
            type="date"
            label="Fecha de expedición"
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{
              ...tfSx,
              ...(campoPendiente(data.fechaExpedicion) && {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#f44336 !important",
                },
                "& .MuiInputLabel-root": {
                  color: "#f44336 !important",
                },
              }),
            }}
            value={data.fechaExpedicion ?? ""}
            onChange={(e) => handleChange("fechaExpedicion", e.target.value)}
          />
        </Grid>

        {/* 5. Ciudad expedición */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            required
            label="Ciudad de expedición"
            fullWidth
            sx={{
              ...tfSx,
              ...(campoPendiente(data.ciudadExpedicion) && {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#f44336 !important",
                },
                "& .MuiInputLabel-root": {
                  color: "#f44336 !important",
                },
              }),
            }}
            value={toTitleCase(data.ciudadExpedicion ?? "")}
            onChange={(e) => handleChange("ciudadExpedicion", toTitleCase(e.target.value))}
          />
        </Grid>

        {/* 6. Género */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            required
            select
            label="Género"
            fullWidth
            sx={tfSx}
            value={data.genero ?? ""}
            onChange={(e) => handleChange("genero", e.target.value)}
          >
            <MenuItem value="">Seleccione…</MenuItem>
            <MenuItem value="Femenino">Femenino</MenuItem>
            <MenuItem value="Masculino">Masculino</MenuItem>
          </TextField>
        </Grid>

        {/* 7. Grupo étnico */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            label="Grupo étnico"
            fullWidth
            sx={tfSx}
            value={data.grupoEtnico ?? ""}
            onChange={(e) => handleChange("grupoEtnico", e.target.value)}
          >
            {["Ninguno", "Caucásico", "Afrocolombiano", "Indígena", "Raizal", "Mestizo"].map(n => (
              <MenuItem key={n} value={n}>{n}</MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* 8. Fecha nacimiento (ISO yyyy-mm-dd) */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            required
            type="date"
            label="Fecha de nacimiento"
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={tfSx}
            value={data.fechaNacimiento ?? ""}
            onChange={(e) => handleChange("fechaNacimiento", e.target.value)}
          />
        </Grid>

        {/* 9. Lugar nacimiento */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            required
            label="Lugar de nacimiento"
            fullWidth
            sx={tfSx}
            value={toTitleCase(data.lugarNacimiento ?? "")}
            onChange={(e) => handleChange("lugarNacimiento", toTitleCase(e.target.value))}
          />
        </Grid>

        {/* 10. Nivel estudios */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            label="Nivel de estudios"
            fullWidth
            sx={tfSx}
            value={data.nivelEstudios ?? ""}
            onChange={(e) => handleChange("nivelEstudios", e.target.value)}
          >
            {["Primaria", "Bachillerato", "Técnica", "Tecnología", "Universitario", "Postgrado", "Maestría"].map(n => (
              <MenuItem key={n} value={n}>{n}</MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* 11. Título profesional */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Título profesional"
            fullWidth
            sx={tfSx}
            value={toTitleCase(data.tituloProfesional ?? "")}
            onChange={(e) => handleChange("tituloProfesional", toTitleCase(e.target.value))}
          />
        </Grid>

        {/* 12. Estado civil */}
        <Grid item xs={12} sm={6} md={3}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5
            }}
          >
            <TextField
              required
              select
              label="Estado civil"
              fullWidth
              sx={tfSx}
              value={data.estadoCivil ?? ""}
              onChange={(e) =>
                handleChange(
                  "estadoCivil",
                  e.target.value
                )
              }
            >
              {[
                "Soltero",
                "Casado",
                "Viudo",
                "Separado",
                "Unión Libre"
              ].map((n) => (
                <MenuItem
                  key={n}
                  value={n}
                >
                  {n}
                </MenuItem>
              ))}
            </TextField>

            <Tooltip
              arrow
              title="Importante: El estado civil registrado en este 
              formulario debe coincidir con la información reportada 
              por su empleador. Para que cualquier actualización sea 
              válida y pueda ser tenida en cuenta en coberturas y 
              beneficios asociados, especialmente en la cobertura 
              funeraria, es necesario que primero gestione la 
              actualización de este dato ante su empresa."
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: "15px",
                    maxWidth: 250,
                    bgcolor: "rgba(0, 0, 0, 0.85)",
                    color: "#fff",
                    borderRadius: "10px",
                    padding: "10px 12px"
                  }
                }
              }}

            >
              <IconButton
                size="small"
                sx={{
                  color: "#6b7280",
                  mt: 0.5
                }}
              >
                <InfoOutlinedIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>


        {/* 13. Cabeza familia */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            label="Cabeza de familia"
            fullWidth
            sx={tfSx}
            value={data.cabezaFamilia ?? ""}
            onChange={(e) => handleChange("cabezaFamilia", e.target.value)}
          >
            <MenuItem value="No">No</MenuItem>
            <MenuItem value="Si">Sí</MenuItem>
          </TextField>
        </Grid>

        {/* 14. Personas a cargo */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            type="number"
            label="Número de personas a cargo"
            fullWidth
            sx={tfSx}
            value={data.personasCargo ?? ""}
            onChange={(e) => handleChange("personasCargo", e.target.value)}
          />
        </Grid>

        {/* 15. Tipo vivienda */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            label="Tipo de vivienda"
            fullWidth
            sx={tfSx}
            value={data.tipoVivienda ?? ""}
            onChange={(e) => handleChange("tipoVivienda", e.target.value)}
          >
            {["Arrendada", "Propia", "Familiar", "Leasing"].map(n => (
              <MenuItem key={n} value={n}>{n}</MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* 16. Estrato */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            label="Estrato económico"
            fullWidth
            sx={tfSx}
            value={data.estrato ?? ""}
            onChange={(e) => handleChange("estrato", e.target.value)}
          >
            {[1, 2, 3, 4, 5, 6].map(n => (
              <MenuItem key={n} value={String(n)}>{n}</MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* 17. Departamento */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            required
            select
            label="Departamento"
            fullWidth
            sx={{
              ...tfSx,
              ...(campoPendiente(data.departamento) && {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#f44336 !important",
                },
                "& .MuiInputLabel-root": {
                  color: "#f44336 !important",
                },
              }),
            }}
            value={data.departamento ?? ""}
            onChange={(e) => handleChange("departamento", e.target.value)}
          >
            {[
              "Amazonas", "Antioquia", "Arauca", "Atlántico", "Bolívar", "Boyacá", "Caldas",
              "Caquetá", "Casanare", "Cauca", "Cesar", "Chocó", "Córdoba", "Cundinamarca",
              "Guainía", "Guaviare", "Huila", "La Guajira", "Magdalena", "Meta", "Nariño",
              "Norte de Santander", "Putumayo", "Quindío", "Risaralda",
              "San Andrés y Providencia", "Santander", "Sucre", "Tolima",
              "Valle del Cauca", "Vaupés", "Vichada"
            ].map(d => (
              <MenuItem key={d} value={d}>{d}</MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* 18. Municipio */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            required
            label="Municipio"
            fullWidth
            sx={tfSx}
            value={toTitleCase(data.municipio ?? "")}
            onChange={(e) => handleChange("municipio", toTitleCase(e.target.value))}
          />
        </Grid>

        {/* 19. Barrio */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            required
            label="Barrio"
            fullWidth
            sx={{
              ...tfSx,
              ...(campoPendiente(data.barrio) && {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#f44336 !important",
                },
                "& .MuiInputLabel-root": {
                  color: "#f44336 !important",
                },
              }),
            }}
            value={toTitleCase(data.barrio ?? "")}
            onChange={(e) => handleChange("barrio", toTitleCase(e.target.value))}
          />
        </Grid>

        {/* 20. Dirección */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            required
            label="Dirección"
            fullWidth
            sx={tfSx}
            value={toTitleCase(data.direccion ?? "")}
            onChange={(e) => handleChange("direccion", toTitleCase(e.target.value))}
          />
        </Grid>

        {/* 21. Teléfono */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Teléfono fijo"
            fullWidth
            sx={tfSx}
            value={data.telefonoFijo ?? ""}
            onChange={(e) =>
              handleChange("telefonoFijo", e.target.value.replace(/\D/g, ""))
            }
            error={!isValidTelefonoFijo(data.telefonoFijo)}
            helperText={
              !isValidTelefonoFijo(data.telefonoFijo)
                ? "Debe contener entre 7 y 10 dígitos"
                : ""
            }
            inputProps={{
              inputMode: "numeric",
              maxLength: 10,
            }}
          />
        </Grid>


        {/* 22. Celular */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            required
            label="Número de WhatsApp"
            fullWidth
            sx={tfSx}
            value={data.celular ?? ""}
            onChange={(e) =>
              handleChange("celular", e.target.value.replace(/\D/g, ""))
            }
            error={!!data.celular && !isValidCelular(data.celular)}
            helperText={
              !!data.celular && !isValidCelular(data.celular)
                ? "Debe ser un número WhatsApp válido."
                : ""
            }
            inputProps={{
              inputMode: "numeric",
              maxLength: 10,
            }}
          />
        </Grid>

        {/* 23. Correo */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            required
            label="Correo electrónico"
            fullWidth
            sx={tfSx}
            value={data.correoPersonal ?? ""}
            onChange={(e) => handleChange("correoPersonal", e.target.value)}
            error={!isValidEmail(data.correoPersonal)}
            helperText={
              !isValidEmail(data.correoPersonal)
                ? "Ingrese un correo electrónico válido"
                : ""
            }
          />
        </Grid>

        {/* 24. Actividad económica */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            disabled
            label="Actividad económica"
            fullWidth
            sx={tfSx}
            value={data.actividadEconomica ?? ""}
            onChange={(e) => handleChange("actividadEconomica", e.target.value)}
          />
        </Grid>

        {/* 25. CIIU */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            disabled
            label="Código CIIU"
            fullWidth
            sx={tfSx}
            value={data.codigoCIIU ?? ""}
            onChange={(e) => handleChange("codigoCIIU", e.target.value)}
          />
        </Grid>

        {/* 26. Empresa */}
        <Grid item xs={12} md={9}>
          <TextField
            select
            label="Empresa contratante"
            fullWidth
            sx={tfSx}
            value={data.empresaContratante ?? ""}
            onChange={(e) => handleChange("empresaContratante", e.target.value)}
          >
            {[
              "ManpowerGroup", "Antioquia Gold Ltd", "Arthrex Colombia S.A.S",
              "Asocreto", "ATW Internacional", "Caf", "Colosal Restaurante", "Dekogift", "Dexco", "Dimatex",
              "Edelman Colombia", "Explomin Perforaciones S.A.S", "FibTex S.A.S",
              "Fudes", "Fondo de Empleados de ManpowerGroup",
              "Glory Global Solutions (Colombia) S.A.S", "GTD", "GHL Opco S.A.S",
              "Hogar y Moda S.A.S", "Holding Hotelera GHL S.A.S", "Leanport S.A.S", "Logística GHL S.A.S",
              "Mane", "Mobilize Financial Services", "Mobilize Lease & Co",
              "Nuvant Global", "Plan Rombo S.A.", "Pronto Rental", "Quimtia",
              "Samsung", "Scribe", "Shiplilly Latam Colombia S.A.S", "Ticsa Colombia", "VitaA"
            ].map(n => (
              <MenuItem key={n} value={n}>{n}</MenuItem>
            ))}
          </TextField>
        </Grid>

      </Grid>

      {/* BOTONES */}
      <Box display="flex" justifyContent="center" gap={3} mt={4}>
        <Button
          variant="outlined"
          size="large"
          onClick={back}
          sx={{ borderRadius: "20px", px: 4, textTransform: "none", fontSize: "1rem" }}
        >
          Atrás
        </Button>

        <Button
          variant="contained"
          size="large"
          disabled={!isStepValid()}   // 🚫 no avanza si no es válido
          onClick={continuar}
          sx={{
            borderRadius: "20px",
            px: 4,
            textTransform: "none",
            fontSize: "1rem"
          }}
        >
          Siguiente
        </Button>
      </Box>

    </Box>
  );
}
